import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

// local imports
import { IFilteredGymSalonList } from 'types/GymType'
import DateUtil from 'utils/DateUtil'
import DatePickerIcon from 'components/ui/DatePickerIcon'

interface ModalHeaderProps {
  availableDateRange: { start: number; end: number }
  filterDate: Date
  setFilterDate: Dispatch<SetStateAction<Date>>
  salon: IFilteredGymSalonList
  setTableWeek: Dispatch<SetStateAction<{ start: Date; end: Date }>>
  selectedDateStr?: string
}

export default function ReservationModalHeader({
  availableDateRange: availableDates,
  selectedDateStr,
  filterDate,
  setFilterDate,
  setTableWeek,
  salon,
}: ModalHeaderProps) {
  const { t } = useTranslation()
  const {
    name,
    reservationAndCapacityStr,
    floor: { locationName, cityName, districtName, countryName, blockName, name: floorName },
  } = salon

  // Variables
  const salonLocationName = `${locationName}, ${districtName}, ${cityName}, ${countryName}, ${blockName}, ${floorName}`

  /* Handle Reservation Date change input */
  const handleDateChange = (e: any) => {
    if (e.value) {
      const { start, end } = DateUtil.getWeekStartEndFromDate(e.value)
      setTableWeek({ start, end })
      setFilterDate(e.value)
    }
  }

  return (
    <GymSalonHeaderInfo>
      <div className="salon-info">
        <div className="salon-summary">
          <span className="salon-name">{name}</span>
          <span className="salon-info-text"> {salonLocationName || '---'} </span>
        </div>
      </div>

      <div className="d-flex flex-column align-items-center justify-content-end">
        {!!selectedDateStr ? (
          <div className="calendar-notify">{t('calendar.selected-day-info-text', { date: selectedDateStr })}</div>
        ) : null}
      </div>

      <div className="appointment-date">
        <div className="capacity-container">
          <i className="pi pi-users"></i>
          <span>{reservationAndCapacityStr}</span>
        </div>
        <DatePickerIcon
          idName="gymSalonModalReservationDate"
          showOtherMonths
          selectOtherMonths
          showOnFocus
          appendTo={document.body}
          disabledDays={[]}
          defaultDate={filterDate}
          minDate={DateUtil.getPastDate(availableDates.start)}
          maxDate={DateUtil.getFutureDate(availableDates.end)}
          label={t('input-texts.appointment-date')}
          onSelectedDateChange={handleDateChange}
          onFocus={() => {}}
        />
      </div>
    </GymSalonHeaderInfo>
  )
}

const GymSalonHeaderInfo = styled.div`
  margin: 0.5rem 0 0.5rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .salon-info {
    position: relative;
    width: 360px;
    display: flex;
    align-items: flex-start;

    .salon-summary {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      margin-right: 40px;

      .salon-info-text {
        font-size: 12px;
        font-weight: 500;
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }

      .salon-name {
        font-size: 14px;
        font-weight: 700;
      }
    }
  }

  .capacity-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 0.5rem;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    column-gap: 4px;
  }

  .calendar-notify {
    max-width: 410px;
    margin: 1rem 0;
    font-size: 14px;
    color: #222e47;
    line-height: 17px;
    font-weight: 700;
  }

  .appointment-date {
    min-width: 265px;
  }
`
